import React, { useRef, useState, useEffect, SyntheticEvent } from 'react';
import { Location } from 'history';
import { getToken } from 'utils/queryHelper';
import { checkUpdatePasswordRules, IPasswordAlertType } from 'utils/password';
import ErrorMessage from 'components/common/ErrorMessage';
import { translationString } from 'components/screens/ResetPassword/translation';
import './styles.scss';

export interface IOwnProps {
  location: Location;
  message: string;
  updatePasswordSuccess: Nullable<boolean>;
  updatePassword: (password: string, token: string) => Promise<void>;
  errorMessage: Nullable<string>;
  isSubmitting?: boolean;
  language?: string;
  setLanguage?: (val: string) => void;
}

interface IState {
  isConfirmBlur: boolean;
  isValid: boolean;
  password: string;
  confirmPassword: string;
  errors: IPasswordAlertType[];
}

interface ITranslateErrorProps {
  language?: string;
  errors: IPasswordAlertType[];
}

const ErrorBlock = (props: ITranslateErrorProps) => {
  const { language = 'english', errors } = props;
  const { errors: translatedErrors } = translationString;
  return errors.length > 0 ? (
    <div styleName="error-block" data-testid="error-block">
      {errors.map((err: IPasswordAlertType) => {
        const key = `error-${err.type}-${translatedErrors[err.type][language]}`;
        return (
          <div styleName="error-message" key={key}>
            <span>{`Error: ${translatedErrors[err.type][language]}`}</span>
          </div>
        );
      })}
    </div>
  ) : null;
};

const UpdatePassword = (props: IOwnProps) => {
  const defaultState = {
    isConfirmBlur: false,
    isValid: false,
    password: '',
    confirmPassword: '',
    errors: [],
    translateErrors: [],
  };

  const {
    updatePassword,
    location,
    updatePasswordSuccess,
    message,
    errorMessage,
    isSubmitting,
    language = 'english',
    setLanguage,
  } = props;

  const passwordInput = useRef<HTMLInputElement>(null);
  const [state, setState] = useState<IState>(defaultState);
  const { isConfirmBlur, isValid, password, confirmPassword, errors } = state;
  const [linkText, setLinkText] = useState<string>('En Español');
  const onConfirmationChange = (
    password: string,
    confirmPassword: string,
  ): IPasswordAlertType[] => {
    return checkUpdatePasswordRules(password, confirmPassword, errors);
  };

  const checkUpdatePasswordIsValid = (
    password: string,
    confirmPassword: string,
  ): boolean => {
    const newErrors = onConfirmationChange(password, confirmPassword);
    const isValid =
      newErrors.length === 0 && !!password && password === confirmPassword;
    return isValid;
  };

  const handleChangePassword = (e: React.ChangeEvent<HTMLInputElement>) => {
    const password = e.target.value;
    const isValid = checkUpdatePasswordIsValid(password, confirmPassword);
    setState((prevState) => ({
      ...prevState,
      isValid,
      password,
    }));
  };

  const handleChangeConfirmPassword = (
    e: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const confirmPassword = e.target.value;
    const isValid = checkUpdatePasswordIsValid(password, confirmPassword);
    setState((prevState) => ({
      ...prevState,
      isValid,
      confirmPassword,
    }));
  };

  const handleConfirmBlur = () => {
    const errors = onConfirmationChange(password, confirmPassword);
    setState((prevState) => ({
      ...prevState,
      isConfirmBlur: true,
      errors,
    }));
  };

  const handleSubmit = (e: SyntheticEvent<HTMLButtonElement>) => {
    e.preventDefault();
    if (isValid) {
      const token = getToken(location.search);
      updatePassword(password, token);
    }
  };

  const handleLanguageChange = (e: React.MouseEvent<HTMLElement>) => {
    e.preventDefault();
    if (setLanguage) {
      if (language === 'english') {
        setLanguage('spanish');
        setLinkText('In English');
      } else {
        setLanguage('english');
        setLinkText('En Español');
      }
    }
  };

  useEffect(() => {
    if (passwordInput.current) {
      passwordInput.current!.focus();
    }
  }, []);

  if (updatePasswordSuccess) {
    return (
      <div styleName="wrapper" data-testid="update-password">
        <div styleName="logo-pear" />
        <div styleName="title">{translationString.title[language]}</div>
        <div styleName="error-block">
          <div styleName="error-message">
            <span>{message}</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div styleName="wrapper" data-testid="update-password">
      <div styleName="inside-wrapper">
        <div styleName="logo-pear" />
        <div styleName="title">{translationString.title[language]}</div>
        {errorMessage && <ErrorMessage message={errorMessage} />}
        {isConfirmBlur && <ErrorBlock errors={errors} language={language} />}
        <div styleName="instruction">
          {translationString.instruction.paragraph[language]}
          <ul>
            <li>{translationString.instruction.uppercase[language]}</li>
            <li>{translationString.instruction.specialChar[language]}</li>
          </ul>
        </div>
        {setLanguage && (
          <div styleName="translation-link">
            <a styleName="link" href="" onClick={handleLanguageChange}>
              {linkText}
            </a>
          </div>
        )}
        <form>
          <div>
            <input
              type="password"
              placeholder={translationString.newPwd[language]}
              styleName="form-input"
              onChange={handleChangePassword}
              value={password}
              ref={passwordInput}
              data-testid="input-password"
            />

            <input
              data-testid="input-password-confirmation"
              type="password"
              placeholder={translationString.confirmPwd[language]}
              styleName="form-input"
              onChange={handleChangeConfirmPassword}
              onBlur={handleConfirmBlur}
              value={confirmPassword}
            />
            <button
              data-testid="submit-password"
              onClick={handleSubmit}
              type="submit"
              styleName="submit-btn"
              disabled={!isValid || isSubmitting === true}
            >
              {translationString.submit[language]}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default UpdatePassword;
